import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import {
  Button,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import { memo, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import Breadcrumbs from '../../components/Breadcrumb'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import ratePlanPagesRoleManagement from '../../staticRoleManagement/ratePlanRoleManagement'
import {
  RoomTypeBrief,
  RoomTypeFilters,
  RoomTypeFiltersActivation,
  RoomTypeFiltersExisting,
  RoomTypeFiltersType
} from '../../types/roomtype'
import useStyles from './styles/RateplansHeader.styles'

interface SelectItems {
  value: string
  lable: string
}

interface RateplansHeaderProps {
  setroomType: React.Dispatch<any>
  roomType: any
  inputValue: string | undefined
  setinputVal: (val: string) => void
  accommodationName: string
  isRoleAdmin: boolean
  filters: RoomTypeFilters
  setFilters: React.Dispatch<React.SetStateAction<RoomTypeFilters>>
}

const RateplansHeader: React.FC<RateplansHeaderProps> = ({
  setroomType,
  roomType,
  inputValue,
  setinputVal,
  accommodationName,
  isRoleAdmin,
  filters,
  setFilters
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const params = useParams<{ accommodationID: string; propertyCode: string }>()

  const { data, loading: loadingForRoomTypes } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      propertyCode: params.propertyCode,
      showDeletedRoomTypes: isRoleAdmin ? true : false,
      showDeactivedRoomTypes: true
    }
  })

  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Accommodations'), link: '/accommodations' },
      {
        label: accommodationName || '',
        link: `/accommodations/${params.accommodationID}/${params.propertyCode}/details`
      },
      {
        label: getTranslate('rateplans')
      }
    ],
    [data, accommodationName]
  )

  const hasAccessToCreateRatePlan = ratePlanPagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/rate-plans/create'
    )
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  const menuItemsForType: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'virtual', lable: 'Virtual' },
    { value: 'physical', lable: 'Physical' }
  ]

  const menuItemsForExistance: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'deleted', lable: 'Deleted' },
    { value: 'existing', lable: 'Existing' }
  ]

  const menuItemsForActivation: SelectItems[] = [
    { value: 'all', lable: 'All' },
    { value: 'active', lable: 'Active' },
    { value: 'deactive', lable: 'Deactive' }
  ]

  return (
    <>
      <div className={classes.backitemContainer}>
        <Breadcrumbs levels={cachedLevels} loading={loadingForRoomTypes} />
      </div>
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate(isSmallScreen ? 'Rate Plans List' : 'Rate Plans')}
        </Typography>
        {hasAccessToCreateRatePlan && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to={`${location.pathname
              .replace('/list', '')
              .replace('/en', '')
              .replace('/fa', '')}/create`}>
            {getTranslate(isSmallScreen ? 'Add' : 'Add Rateplan')}
          </Button>
        )}
      </div>
      <div className={classes.root}>
        <div>
          <AutocompleteWithLabel
            placeholder={getTranslate('Please select a roomtype')}
            value={roomType}
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            options={
              data?.roomTypes.map((item) => ({
                label: item.name + ` (${item.displayRoomTypeCode})`,
                value: item.roomTypeCode || '',
                code: item.displayRoomTypeCode
              })) || []
            }
            onChange={(_, value: any) => {
              setroomType(value)
            }}
            fullWidth
            noHelperText
            inputSize="small"
            loading={loadingForRoomTypes}
            loadingText={getTranslate('Loading') + '...'}
          />
        </div>
        <div>
          <InputWithLabel
            placeholder={getTranslate('Search by name, code or type')}
            autoComplete="off"
            noHelperText
            fullWidth
            startIcon={<SearchIcon />}
            inputSize="small"
            value={inputValue}
            onChange={(event) =>
              setinputVal(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }
          />
        </div>
      </div>
      {isRoleAdmin && (
        <div className={classes.filters}>
          <SelectWithLabel
            fullWidth
            value={filters.type}
            onChange={(event) => {
              setFilters({
                ...filters,
                type: event.target.value as RoomTypeFiltersType
              })
            }}
            size="small"
            label={getTranslate('Type')}>
            {menuItemsForType.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            value={filters.existing}
            fullWidth
            onChange={(event) => {
              setFilters({
                ...filters,
                existing: event.target.value as RoomTypeFiltersExisting
              })
            }}
            size="small"
            label={getTranslate('Existance')}>
            {menuItemsForExistance.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
          <SelectWithLabel
            value={filters.activation}
            fullWidth
            onChange={(event) => {
              setFilters({
                ...filters,
                activation: event.target.value as RoomTypeFiltersActivation
              })
            }}
            size="small"
            label={getTranslate('Activation')}>
            {menuItemsForActivation.map((item, index) => (
              <MenuItem key={index} value={item.value}>
                {getTranslate(item.lable)}
              </MenuItem>
            ))}
          </SelectWithLabel>
        </div>
      )}
    </>
  )
}

export default memo(RateplansHeader)
